// Single player: set the scenario up once, then let the opponent play Black.
//
// **Call this from `GameShell` and from nowhere else.** The latch below is a
// ref, so it lives on whichever component instance calls the hook: on the
// shell it survives StrictMode's mount-cleanup-mount, and in a child that can
// remount it would be reset — which lays the opening over a board that is
// already being played.

import { useEffect, useRef } from "react";

import { hasScenario, scenarioById, scenarioOpening } from "src/engine/ai/scenarios";
import { freshSeed } from "src/engine/game/seed";
import type { Command } from "src/engine/rules/commands";

export function useScenarioOpening(
  scenario: string | undefined,
  seed: number | undefined,
  teams: readonly number[] | undefined,
  // The shape `scenarioOpening` takes, which is `new-game`'s own field: the
  // flags are resolved in the shell and handed down as a value, never read
  // any nearer the rules.
  ruleset: Extract<Command, { type: "new-game" }>["ruleset"],
  dispatch: (command: Command) => boolean
): void {
  const started = useRef(false);
  useEffect(() => {
    // A real scenario, not the `"run"` marker: that is passed to switch the
    // opponent on, and treating it as an id lays the chess opening over an
    // authored level — or over a run that was just loaded from a save.
    if (!scenario || !hasScenario(scenario) || started.current) return;
    started.current = true;
    // The seed the player asked for, if they did. Two games on one seed are the
    // same game, which is what makes a bug somebody saw once a bug anybody can
    // look at.
    //
    // **Built by `scenarioOpening`, not here.** This used to flatten the table
    // itself and knew only about `fen` and `heaps`, while Restart's copy knew
    // about everything — so a set piece with a lane or a stocked hopper opened
    // without them and only came right if you restarted it.
    //
    // The ruleset the flags chose for a fresh game — `undefined` outside every
    // experiment, so the command is byte-identical to what shipped before.
    dispatch(
      scenarioOpening(scenarioById(scenario), seed ?? freshSeed(), [], teams, ruleset)
    );
  }, [scenario, seed, teams, ruleset, dispatch]);
}
